// ─── ScheduleFields.tsx ───────────────────────────────────────────────────────
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ENUM_PERFORMANCE_CYCLE_TYPE } from "@/utils/enums/performance.enum";
import { useTranslation } from "react-i18next";

// ─── Constants ────────────────────────────────────────────────────────────────
const buildDaysOfWeek = (t: (key: string) => string) => [
  { label: t("company:performance.day_monday_default"), value: 1 },
  { label: t("company:performance.day_tuesday"), value: 2 },
  { label: t("company:performance.day_wednesday"), value: 3 },
  { label: t("company:performance.day_thursday"), value: 4 },
  { label: t("company:performance.day_friday"), value: 5 },
  { label: t("company:performance.day_saturday"), value: 6 },
  { label: t("company:performance.day_sunday"), value: 7 },
];

const buildDaysOfMonth = (t: (key: string) => string) =>
  Array.from({ length: 31 }, (_, i) => ({
    label: `${String(i + 1).padStart(2, "0")}${i === 0 ? t("company:performance.day_of_month_default_suffix") : ""}`,
    value: i + 1,
  }));

const buildMonthsOfYear = (t: (key: string) => string) => [
  { label: t("company:performance.month_january_default"), value: 1 },
  { label: t("company:performance.month_february"), value: 2 },
  { label: t("company:performance.month_march"), value: 3 },
  { label: t("company:performance.month_april"), value: 4 },
  { label: t("company:performance.month_may"), value: 5 },
  { label: t("company:performance.month_june"), value: 6 },
  { label: t("company:performance.month_july"), value: 7 },
  { label: t("company:performance.month_august"), value: 8 },
  { label: t("company:performance.month_september"), value: 9 },
  { label: t("company:performance.month_october"), value: 10 },
  { label: t("company:performance.month_november"), value: 11 },
  { label: t("company:performance.month_december"), value: 12 },
];

const buildQuarterMonths = (t: (key: string) => string) => [
  { label: t("company:performance.quarter_month_1_default"), value: 1 },
  { label: t("company:performance.quarter_month_2"), value: 2 },
  { label: t("company:performance.quarter_month_3"), value: 3 },
];

// ─── Reusable Select ──────────────────────────────────────────────────────────
interface IScheduleSelectProps {
  label: string;
  value: number;
  options: { label: string; value: number }[];
  onChange: (val: number) => void;
}

const ScheduleSelect = ({
  label,
  value,
  options,
  onChange,
}: IScheduleSelectProps) => {
  const { t } = useTranslation(["company"]);
  return (
    <Field>
      <FieldLabel className="uppercase text-xs tracking-wide text-gray-500">
        {label}
      </FieldLabel>
      <Select
        value={String(value)}
        onValueChange={(val) => onChange(Number(val))}
      >
        <SelectTrigger>
          <SelectValue
            placeholder={t("company:performance.schedule_select_placeholder", {
              label: label.toLowerCase(),
            })}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={String(opt.value)}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </Field>
  );
};

// ─── Props ────────────────────────────────────────────────────────────────────
interface IScheduleFieldsProps {
  cycleType: ENUM_PERFORMANCE_CYCLE_TYPE;
  scheduleDay: number;
  scheduleMonth: number;
  onDayChange: (val: number) => void;
  onMonthChange: (val: number) => void;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export const ScheduleFields = ({
  cycleType,
  scheduleDay,
  scheduleMonth,
  onDayChange,
  onMonthChange,
}: IScheduleFieldsProps) => {
  const { t } = useTranslation(["company"]);

  if (cycleType === ENUM_PERFORMANCE_CYCLE_TYPE.WEEKLY) {
    return (
      <ScheduleSelect
        label={t("company:performance.schedule_day_of_week_label")}
        value={scheduleDay}
        options={buildDaysOfWeek(t)}
        onChange={onDayChange}
      />
    );
  }

  if (cycleType === ENUM_PERFORMANCE_CYCLE_TYPE.MONTHLY) {
    return (
      <ScheduleSelect
        label={t("company:performance.schedule_day_of_month_label")}
        value={scheduleDay}
        options={buildDaysOfMonth(t)}
        onChange={onDayChange}
      />
    );
  }

  if (cycleType === ENUM_PERFORMANCE_CYCLE_TYPE.QUARTERLY) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ScheduleSelect
          label={t("company:performance.schedule_start_month_of_quarter_label")}
          value={scheduleMonth}
          options={buildQuarterMonths(t)}
          onChange={onMonthChange}
        />
        <ScheduleSelect
          label={t("company:performance.schedule_day_of_month_label")}
          value={scheduleDay}
          options={buildDaysOfMonth(t)}
          onChange={onDayChange}
        />
      </div>
    );
  }

  if (cycleType === ENUM_PERFORMANCE_CYCLE_TYPE.ANNUALLY) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ScheduleSelect
          label={t("company:performance.schedule_month_of_year_label")}
          value={scheduleMonth}
          options={buildMonthsOfYear(t)}
          onChange={onMonthChange}
        />
        <ScheduleSelect
          label={t("company:performance.schedule_date_of_month_label")}
          value={scheduleDay}
          options={buildDaysOfMonth(t)}
          onChange={onDayChange}
        />
      </div>
    );
  }

  return null;
};
